import { useEffect, useRef } from 'react';

/**
 * useModalA11y — makes an existing modal element behave as an accessible dialog.
 *
 * A11y review 2026-06-30 (HIGH): the canonical Citadel modal pattern
 * (`.modal-overlay` > `.modal` > <ModalCoverBand/> + body) had no dialog
 * semantics, no focus management, and no keyboard close. This hook layers all of
 * that onto the *existing* DOM without changing markup or styling:
 *
 *   1. role="dialog" + aria-modal="true" on the modal container.
 *   2. aria-label (or aria-labelledby) so the dialog has an accessible name.
 *   3. Moves focus into the dialog on mount (first focusable, else the container).
 *   4. Traps Tab / Shift+Tab within the dialog.
 *   5. Escape calls onClose.
 *   6. Restores focus to the previously-focused element on unmount.
 *
 * It is intentionally driven from the resolved `.modal` element rather than
 * owning the markup, so it can be dropped into ModalCoverBand (which renders
 * *inside* `.modal`) and locate its own dialog ancestor — letting every modal
 * that uses the cover band inherit accessibility with no call-site changes.
 *
 * The element is passed by value (not a ref) and lives in the dependency array,
 * so the effect (re-)runs the moment the dialog ancestor is resolved — even
 * when that happens a render after mount.
 *
 * @param dialog    The dialog container element (the `.modal` node), or null
 *                  until it has been resolved.
 * @param onClose   Called on Escape. Should match the modal's close handler.
 * @param options   { label } for aria-label, or { labelledBy } for an element id.
 */
export function useModalA11y(
  dialog: HTMLElement | null,
  onClose: (() => void) | undefined,
  options?: { label?: string; labelledBy?: string },
) {
  // Keep the latest onClose without re-running the effect on every render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const label = options?.label;
  const labelledBy = options?.labelledBy;

  useEffect(() => {
    if (!dialog) return;

    // Remember what had focus so we can restore it on close.
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // --- 1 & 2: dialog semantics + accessible name -------------------------
    // Don't clobber semantics a host element may already set.
    const hadRole = dialog.hasAttribute('role');
    const hadAriaModal = dialog.hasAttribute('aria-modal');
    if (!hadRole) dialog.setAttribute('role', 'dialog');
    if (!hadAriaModal) dialog.setAttribute('aria-modal', 'true');

    let setLabelledBy = false;
    let setLabel = false;
    if (!dialog.hasAttribute('aria-label') && !dialog.hasAttribute('aria-labelledby')) {
      if (labelledBy) {
        dialog.setAttribute('aria-labelledby', labelledBy);
        setLabelledBy = true;
      } else if (label) {
        dialog.setAttribute('aria-label', label);
        setLabel = true;
      }
    }

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    const getFocusable = (): HTMLElement[] =>
      Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector)).filter(
        (el) =>
          el.offsetWidth > 0 ||
          el.offsetHeight > 0 ||
          el === document.activeElement,
      );

    // --- 3: move focus into the dialog -------------------------------------
    // Prefer the first focusable that is not the close ("×") button so the
    // user lands on meaningful content; fall back to the container itself.
    const focusables = getFocusable();
    const firstMeaningful =
      focusables.find((el) => el.getAttribute('aria-label') !== 'Close') ??
      focusables[0];
    if (firstMeaningful) {
      firstMeaningful.focus();
    } else {
      // Make the container programmatically focusable as a last resort.
      if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
      dialog.focus();
    }

    // --- 4 & 5: trap Tab, close on Escape ----------------------------------
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (e.key !== 'Tab') return;

      const items = getFocusable();
      if (items.length === 0) {
        // Nothing focusable — keep focus on the container.
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey) {
        if (active === first || !dialog.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !dialog.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    dialog.addEventListener('keydown', onKeyDown);

    // --- 6: cleanup + focus restore ----------------------------------------
    return () => {
      dialog.removeEventListener('keydown', onKeyDown);
      if (!hadRole) dialog.removeAttribute('role');
      if (!hadAriaModal) dialog.removeAttribute('aria-modal');
      if (setLabelledBy) dialog.removeAttribute('aria-labelledby');
      if (setLabel) dialog.removeAttribute('aria-label');

      // Restore focus only if it is still inside (or was taken by) the dialog,
      // so we don't yank focus away from wherever the user has since moved.
      if (
        previouslyFocused &&
        typeof previouslyFocused.focus === 'function' &&
        document.body.contains(previouslyFocused)
      ) {
        previouslyFocused.focus();
      }
    };
    // Re-run when the dialog element resolves or the accessible name changes.
  }, [dialog, label, labelledBy]);
}
